import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { FaGraduationCap, FaPhone, FaEnvelope, FaUser, FaHome, FaIdCard } from "react-icons/fa";

const AdmissionView = () => {
  const { slug } = useParams();
  const [admission, setAdmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchAdmissionBySlug();
  }, [slug]);

  const fetchAdmissionBySlug = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/admission/${slug}`);
      setAdmission(response.data.data);
    } catch (error) {
      console.error("Error fetching admission:", error);
      if (error.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading admission form...</p>
        </div>
      </div>
    );
  }

  if (notFound || !admission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md text-center">
          <FaGraduationCap className="mx-auto text-6xl text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Admission Form Not Found</h2>
          <p className="text-gray-600 mb-6">
            The admission form you're looking for doesn't exist or has been removed.
          </p>
          <p className="text-sm text-gray-500">
            Please check the URL or contact the administrator for assistance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Admission Form</h1>
                <p className="text-blue-100">
                  Application ID: {admission.slug}
                </p>
              </div>
              <div className="text-right">
                <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  admission.paymentInfo?.status === 'paid' 
                    ? 'bg-green-500 text-white'
                    : admission.paymentInfo?.status === 'pending'
                    ? 'bg-yellow-500 text-white'
                    : 'bg-gray-500 text-white'
                }`}>
                  {admission.paymentInfo?.status?.toUpperCase() || 'DRAFT'}
                </div>
              </div>
            </div>
          </div>

          {/* Student Information */}
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaUser className="mr-2 text-blue-600" />
              Student Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
                <p className="text-gray-900 font-medium">{admission.studentName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Mobile Number</label>
                <p className="text-gray-900 font-medium flex items-center">
                  <FaPhone className="mr-2 text-gray-400" />
                  {admission.mobile || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Email Address</label>
                <p className="text-gray-900 font-medium flex items-center">
                  <FaEnvelope className="mr-2 text-gray-400" />
                  {admission.email || 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Aadhar Card</label>
                <p className="text-gray-900 font-medium flex items-center">
                  <FaIdCard className="mr-2 text-gray-400" />
                  {admission.aadharCard || 'N/A'}
                </p>
              </div>
            </div>

            {/* Parent Information */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaUser className="mr-2 text-blue-600" />
              Parent Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Father's Name</label>
                <p className="text-gray-900 font-medium">{admission.fatherName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Father's Mobile</label>
                <p className="text-gray-900 font-medium flex items-center">
                  <FaPhone className="mr-2 text-gray-400" />
                  {admission.fatherMobile || 'N/A'}
                </p>
              </div>
            </div>

            {/* Academic Information */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaGraduationCap className="mr-2 text-blue-600" />
              Academic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">10th Percentage</label>
                <p className="text-gray-900 font-medium">{admission.tenthPercentage || 'N/A'}%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">12th Percentage</label>
                <p className="text-gray-900 font-medium">{admission.twelfthPercentage || 'N/A'}%</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Graduation Percentage</label>
                <p className="text-gray-900 font-medium">
                  {admission.graduationPercentage ? `${admission.graduationPercentage}%` : 'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">ITI/Diploma</label>
                <p className="text-gray-900 font-medium">{admission.itiDiploma || 'N/A'}</p>
              </div>
            </div>

            {/* College and Course Information */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaGraduationCap className="mr-2 text-blue-600" />
              College & Course Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">College</label>
                <p className="text-gray-900 font-medium">{admission.collegeName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Selected Courses</label>
                <div className="flex flex-wrap gap-2">
                  {admission.selectedCourses?.length > 0 ? (
                    admission.selectedCourses.map((course, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {course.name || course}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">No courses selected</span>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaHome className="mr-2 text-blue-600" />
              Address Information
            </h2>
            <div className="mb-6">
              <p className="text-gray-900 font-medium whitespace-pre-line">
                {admission.address || 'N/A'}
              </p>
            </div>

            {/* Payment Information */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Payment Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Admission Fee</label>
                  <p className="text-2xl font-bold text-green-600">
                    {admission.paymentInfo?.amount ? formatCurrency(admission.paymentInfo.amount) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Payment Status</label>
                  <div className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold ${
                    admission.paymentInfo?.status === 'paid' 
                      ? 'bg-green-100 text-green-800'
                      : admission.paymentInfo?.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {admission.paymentInfo?.status?.toUpperCase() || 'DRAFT'}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Information */}
            <div className="mt-8 pt-6 border-t text-center text-sm text-gray-500">
              <p>Application submitted on {formatDate(admission.submittedAt || admission.createdAt)}</p>
              <p className="mt-2">© 2025 AdmissionDekhoo. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionView;
